import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ChangeDetectionStrategy, Optional, Input, Output, EventEmitter, HostBinding, ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { sanitizeInputForCrossSiteScriptCharactersDom, sanitizeInputForCrossSiteScriptCharactersDescription, stripHtmlToText, nonWhitespaceHtmlValidator, getStatusPageErrorMessage } from '../../../../../_theme/core/_base/curd/utils/validators-util';
import { Incident } from '../../../shared/models/dashboard.models';
import { CustomSelectComponent } from '../../components/custom-select/custom-select.component';
import { IncidentPostmortemComponent } from '../../components/incident-postmortem/incident-postmortem.component';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { finalize, take } from 'rxjs/operators';
import { LayoutUtilsService, MessageType } from '../../../../../_theme/core/_base/curd';
import { environment } from 'src/environments/environment';
import { EditIncidentUpdatePayload, IncidentUpdatePayload, DEFAULT_INCIDENT_UPDATE_PAYLOAD } from '../../models/status-page-api.models';
import { StatusDrawerQuillKeyboardDirective } from '../../directives/status-drawer-quill-keyboard.directive';
import { StatusPageRichTextPipe } from '../../pipes/status-page-rich-text.pipe';
import {
  getIncidentStatusLabel as getIncidentStatusDisplayLabel,
  normalizeIncidentStatusClass
} from '../../models/status-pill.utils';
import {
  compareTimelineEntriesByTimestampDesc,
  formatDateInTimeZone,
  formatRelativeTimeLabel,
  getTimeZoneAbbreviation,
  hasValidTimestamp,
  parseTimestampFromSource
} from '../../models/status-time.utils';
import { normalizeStatusPageRichText } from '../../models/status-page-rich-text-normalizer.utils';
import { StatusPageProfileTimeZoneService } from '../../../services/status-page-profile-time-zone.service';

type DateInput = Date | string | null | undefined;

interface ManageIncidentUpdate {
  updateId?: string | number;
  incidentUpdateId: string | number;
  status?: number | string;
  statusLabel?: string;
  timestamp?: Date | string;
  message: string;
}

@Component({
  selector: 'app-status-page-manage-incident-dialog',
  templateUrl: './manage-incident-dialog.component.html',
  styleUrls: [
    './manage-incident-dialog.component.scss',
    './manage-incident-dialog.component.timeline-quill-actions.scss',
    './manage-incident-dialog.component.dark-tabs.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatDialogModule,
    QuillModule,
    CustomSelectComponent,
    IncidentPostmortemComponent,
    StatusDrawerQuillKeyboardDirective,
    StatusPageRichTextPipe
  ]
})
export class StatusPageManageIncidentDialogComponent implements OnInit {

  readonly messageEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link']
    ]
  };

  @Input() dataInput?: Incident;
  @Input() layout: 'dialog' | 'drawer' = 'dialog';
  @Input() statusReadonly: boolean = false;
  @Input() hidePostUpdateSection: boolean = false;
  @Output() closed = new EventEmitter<any>();
  @Output() refresh = new EventEmitter<void>();
  @Output() toggleExpand = new EventEmitter<void>();
  @ViewChild(IncidentPostmortemComponent) postmortemComponent?: IncidentPostmortemComponent;

  @Input() isExpanded: boolean = false;
  @Input() showExpandToggle: boolean = true;

  @HostBinding('class.drawer-mode') get isDrawerMode() {
    return this.layout === 'drawer';
  }

  get incident() {
    return this.dataInput ?? this.data;
  }

  incidentId?: string | number;
  incidentHasPostmortem: boolean | null = null;

  // Forms
  updateForm!: FormGroup;
  editIncidentForm!: FormGroup;
  resolveForm!: FormGroup;

  // State
  incidentUpdates: ManageIncidentUpdate[] = [];
  editingUpdateId: string | number | null = null;
  editUpdateMessage = '';
  originalEditMessage = '';
  messageLength: number | null = null;
  isLoading = false;
  isSaving = false;
  profileTimeZone: string | null = null;
  isTimeZoneLoading = false;

  activeTab: 'updates' | 'postmortem' = 'updates';

  incidentStatusOptions = [
    { value: 1, label: 'Investigating' },
    { value: 2, label: 'Identified' },
    { value: 3, label: 'Monitoring' },
    { value: 4, label: 'Resolved' }
  ];

  constructor(
    @Optional() public readonly dialogRef: MatDialogRef<StatusPageManageIncidentDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public readonly data: Incident | null,
    private readonly fb: FormBuilder,
    private readonly statusPageService: StatusPageService,
    private readonly authService: AuthService,
    private readonly cd: ChangeDetectorRef,
    private readonly layoutUtilsService: LayoutUtilsService,
    private readonly sanitizer: DomSanitizer,
    private readonly profileTimeZoneService: StatusPageProfileTimeZoneService
  ) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(advancedFormat);
  }

  get timezoneAbbrev(): string {
    return getTimeZoneAbbreviation(this.profileTimeZone);
  }

  ngOnInit(): void {
    const effectiveData = this.dataInput ?? this.data;
    if (!effectiveData) {
      return;
    }

    this.incidentId = effectiveData.id;
    this.incidentHasPostmortem = this.resolveIncidentHasPostmortem(effectiveData);
    this.initForms(effectiveData);
    this.loadProfileTimeZone();
    this.loadIncidentUpdates();
  }

  private loadIncidentUpdates() {
    if (!this.incidentId) return;
    this.isLoading = true;
    const token = this.authService.getToken();
    this.statusPageService.getIncidentUpdatesV3(token, this.incidentId).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cd.detectChanges();
      })
    ).subscribe({
      next: (updates) => {
        const updatesArray = this.resolveUpdatesArray(updates);

        this.incidentUpdates = updatesArray
          .map((update: any) => ({
            updateId: update.updateId || update.incidentUpdateId || update.id,
            incidentUpdateId: update.incidentUpdateId || update.updateId || update.id,
            status: update.status,
            statusLabel: update.statusName || this.getIncidentStatusLabel(update.status),
            timestamp: parseTimestampFromSource(update),
            message: normalizeStatusPageRichText(update.message || update.description || '')
          }) as ManageIncidentUpdate)
          .sort((a: ManageIncidentUpdate, b: ManageIncidentUpdate) =>
            compareTimelineEntriesByTimestampDesc(
              a,
              b,
              (entry) => entry?.timestamp,
              (entry) => entry?.incidentUpdateId
            )
          );

        this.cd.detectChanges();
      },
      error: () => {}
    });
  }

  private resolveUpdatesArray(updates: any): any[] {
    if (Array.isArray(updates)) {
      return updates;
    }
    if (Array.isArray(updates?.data)) {
      return updates.data;
    }
    if (Array.isArray(updates?.updates)) {
      return updates.updates;
    }
    return [];
  }

  private initForms(effectiveData: Incident) {
    const incidentStatusValue = this.mapStatusValue(effectiveData.status, this.incidentStatusOptions, 1);

   this.updateForm = this.fb.group({
      status: [{ value: incidentStatusValue, disabled: this.statusReadonly }, Validators.required],
      message: ['', [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription, this.maxTextLengthValidator(5000)]],
      notifySubscribers: [true]
    });

    this.editIncidentForm = this.fb.group({
      title: [effectiveData.title, [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDom(this.sanitizer)]],
      description: [effectiveData.description || '', [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription]]
    });

    this.resolveForm = this.fb.group({
      resolution: ['']
    });
  }

  private mapStatusValue(
    status: unknown,
    options: { value: number; label: string }[],
    fallback: number
  ): number {
    if (typeof status === 'number' && Number.isFinite(status)) {
      return status;
    }
    if (typeof status === 'string') {
      const normalized = status.toLowerCase().replaceAll(/[\s_-]/g, '');
      const match = options.find(option => option.label.toLowerCase().replaceAll(/[\s_-]/g, '') === normalized);
      if (match) {
        return match.value;
      }
    }
    return fallback;
  }

  get showPostmortemTab(): boolean {
    return this.isIncidentResolved(this.incident?.status);
  }

  switchTab(tab: 'updates' | 'postmortem'): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
    }
  }
  onSavePostmortem(): void {
    this.postmortemComponent?.savePostmortem();
  }

  onPostmortemSaved(): void {
    this.refresh.emit();
    if (this.layout === 'drawer') {
      return;
    }
    this.close({ action: 'postmortem-saved' });
  }

  onDeletePostmortem(): void {
    this.postmortemComponent?.deletePostmortem();
  }

  get isPostmortemSaving(): boolean {
    return !!(this.postmortemComponent?.isSaving || this.postmortemComponent?.isPublishing || this.postmortemComponent?.isDeleting);
  }

  get isPostmortemLoading(): boolean {
    return !!this.postmortemComponent?.isLoading;
  }

  get hasPostmortem(): boolean {
    return !!this.postmortemComponent?.postmortemId;
  }

  get isPostmortemValid(): boolean {
    if (!this.postmortemComponent) return true;
    return this.postmortemComponent.isFormValid;
  }

  get canSavePostmortem(): boolean {
    if (!this.postmortemComponent) return false;
    return this.postmortemComponent.canSavePostmortem;
  }

  get isUpdateHistoryReadonly(): boolean {
    return this.statusReadonly || this.hidePostUpdateSection;
  }

  onCancel() {
    this.close();
  }

  triggerChangeDetection() {
    this.cd.detectChanges();
  }

  onToggleExpand() {
    this.toggleExpand.emit();
  }

  onPostUpdate() {
    if (this.isSaving) return;
    this.updateForm.markAllAsTouched();
    if (this.updateForm.invalid) {
      return;
    }

    const payload: IncidentUpdatePayload = {
      ...DEFAULT_INCIDENT_UPDATE_PAYLOAD,
      message: this.updateForm.value.message,
      status: this.getPostUpdateStatusValue(),
      notifySubscribers: this.updateForm.get('notifySubscribers')?.value === true
    };

    const token = this.authService.getToken();
    const incidentId = this.incidentId ?? this.dataInput?.id ?? this.data?.['id'];
    if (!incidentId) {
      return;
    }
    this.isSaving = true;
    this.statusPageService.createIncidentUpdateV3(token, incidentId, payload).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.layoutUtilsService.showActionNotification('Incident updated successfully', MessageType.Create, environment.TOAST_MESSAGE_TIME, true, false);
        this.close({ action: 'updated' });
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error posting incident update');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isSaving = false;
        this.cd.detectChanges();
      }
    });
  }


  startEditUpdate(update: any) {
    if (this.isUpdateHistoryReadonly) {
      return;
    }
    this.editingUpdateId = update.incidentUpdateId;
    this.editUpdateMessage = update.message;
    this.originalEditMessage = update.message;
  }

  cancelEditUpdate() {
    this.editingUpdateId = null;
    this.editUpdateMessage = '';
  }

  saveEditUpdate(update: any) {
    if (this.isUpdateHistoryReadonly) {
      return;
    }
    if (!this.editUpdateMessage || !this.incidentId) return;

    const token = this.authService.getToken();
    const updateId = update.updateId || update.incidentUpdateId;

    const payload: EditIncidentUpdatePayload = {
      message: this.editUpdateMessage,
      status: this.getExistingUpdateStatusValue(update)
    };

    this.statusPageService.updateIncidentUpdateV3(token, this.incidentId, updateId, payload).subscribe({
      next: () => {
        this.layoutUtilsService.showActionNotification('Update edited successfully', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        update.message = normalizeStatusPageRichText(this.editUpdateMessage);
        this.cancelEditUpdate();
        this.refresh.emit();
        this.cd.detectChanges();
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error updating incident update');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Delete, environment.TOAST_MESSAGE_TIME, true, false);
      }
    });
  }

  getIncidentStatusLabel(status: any): string {
    return getIncidentStatusDisplayLabel(status, 'monitoring');
  }

  getTimelineStatusClass(update: { statusLabel?: unknown; status?: unknown } | null | undefined): string {
    return normalizeIncidentStatusClass(update?.statusLabel ?? update?.status, 'monitoring');
  }

  getTimelineStatusLabel(update: { statusLabel?: unknown; status?: unknown } | null | undefined): string {
    const rawStatus = update?.statusLabel ?? update?.status;
    if (rawStatus === null || rawStatus === undefined) {
      return 'Update';
    }
    if (typeof rawStatus === 'string' && rawStatus.trim().length === 0) {
      return 'Update';
    }
    return getIncidentStatusDisplayLabel(rawStatus, 'monitoring');
  }

  onContentChanged(event: any) {
    const length = this.getPlainTextLength(event.html);
    this.messageLength = length === 0 ? null : length;
    this.cd.detectChanges();
  }

  onBooleanControlEnter(event: KeyboardEvent, controlName: 'notifySubscribers'): void {
    event.preventDefault();
    const control = this.updateForm.get(controlName);
    if (!control || control.disabled) {
      return;
    }
    control.setValue(!control.value);
    control.markAsDirty();
    control.markAsTouched();
  }

  private getPlainTextLength(value: unknown): number {
    return stripHtmlToText(value).length;
  }

  private maxTextLengthValidator(maxLength: number) {
    return (control: AbstractControl) => {
      const length = this.getPlainTextLength(control?.value);
      return length > maxLength ? { maxTextLength: { requiredLength: maxLength, actualLength: length } } : null;
    };
  }

   private getPostUpdateStatusValue(): number {
    const fallback = this.getCurrentIncidentStatusValue();
    if (this.statusReadonly) {
      return fallback;
    }
    return this.mapStatusValue(this.updateForm.get('status')?.value, this.incidentStatusOptions, fallback);
  }

  private getExistingUpdateStatusValue(update: { status?: unknown; statusLabel?: unknown } | null | undefined): number {
    return this.mapStatusValue(
      update?.status ?? update?.statusLabel,
      this.incidentStatusOptions,
      this.getCurrentIncidentStatusValue()
    );
  }

  private getCurrentIncidentStatusValue(): number {
    return this.mapStatusValue(this.incident?.status, this.incidentStatusOptions, 1);
  }
  
  private resolveIncidentHasPostmortem(incident: any): boolean | null {
    if (!incident) {
      return null;
    }
    if (typeof incident.hasPostmortem === 'boolean') {
      return incident.hasPostmortem;
    }
    if (typeof incident.has_postmortem === 'boolean') {
      return incident.has_postmortem;
    }
    if (typeof incident.hasPostMortem === 'boolean') {
      return incident.hasPostMortem;
    }
    return null;
  }

  getStatusClass(status: any): string {
    return normalizeIncidentStatusClass(status, 'monitoring');
  }

  isIncidentResolved(status: any): boolean {
    if (typeof status === 'number') {
      return status === 4;
    }
    if (typeof status === 'string') {
      return status.toLowerCase().includes('resolved');
    }
    return false;
  }
  private close(result?: any) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.closed.emit(result);
  }

  formatDateInAccountTz(date: DateInput, format: string = 'MMM d, y h:mm a'): string {
    return formatDateInTimeZone(date, this.profileTimeZone, format);
  }

  getTimelineDateTimeLabel(date: DateInput): string {
    return this.formatDateInAccountTz(date) || 'Time unavailable';
  }

  showTimezoneForDate(date: DateInput): boolean {
    return hasValidTimestamp(date) && !!this.timezoneAbbrev;
  }

  getRelativeTimeLabel(date: DateInput): string {
    return formatRelativeTimeLabel(date, new Date(), this.profileTimeZone);
  }

  private loadProfileTimeZone(): void {
    this.profileTimeZoneService.getProfileTimeZone().pipe(take(1)).subscribe((timeZone) => {
      this.profileTimeZone = timeZone;
      this.cd.markForCheck();
    });
  }
}




